// Phase 25a — Smart field-value renderer.
//
// Replaces the raw `JSON.stringify(value)` fallback that was previously
// used inside FormDetails tables and generic rows. User-facing cells
// never receive a JSON blob again. If a field is an object we route it
// through a type-aware component (AttachmentChip, AdminStatusChip,
// NestedObjectTable, KeyValueBlock, …).
//
// Hard rules (do NOT weaken without updating `test_no_raw_json_in_ui.py`):
//   1. Never call `JSON.stringify` on data that reaches the DOM.
//   2. Recursion is bounded at MAX_DEPTH = 5 — deeper structures render
//      as a `[Nested data]` label so a corrupt record cannot blow up
//      the detail page.
//   3. Primitives (string / number / boolean / null / undefined /
//      empty-string) use the existing visual (— for empty).
//
import { Paperclip, Clock } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const MAX_DEPTH = 5;

/* --------- utils --------- */
function _formatBytes(bytes) {
  if (!bytes || bytes <= 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
function _truncate(s, n) {
  if (!s) return s;
  if (s.length <= n) return s;
  return `${s.slice(0, n - 1)}…`;
}
const _ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;
function _isLikelyAttachment(obj) {
  if (!obj || typeof obj !== "object") return false;
  const hasFilename = typeof obj.filename === "string" && obj.filename.length > 0;
  const hasUrl = typeof obj.url === "string" || typeof obj.download_url === "string";
  const hasId = typeof obj.attachment_id === "string";
  return (hasFilename || hasId) && (hasUrl || hasId);
}
function _isAdminStatus(obj) {
  if (!obj || typeof obj !== "object") return false;
  return (
    (typeof obj.status === "string" || typeof obj.current === "string")
    && Array.isArray(obj.history)
  );
}
function _humanLabel(k) {
  return (k || "")
    .replace(/_/g, " ")
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

/* --------- Attachment chip --------- */
export function AttachmentChip({ filename, url, sizeBytes }) {
  const display = _truncate(filename || "attachment", 32);
  const sz = _formatBytes(sizeBytes);
  const href = url ? (url.startsWith("http") ? url : `${BACKEND}${url}`) : null;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-[#7a1f2b]/40 bg-[#f9e8ec] text-[#7a1f2b] text-xs"
      data-testid={`attachment-chip-${filename}`}
      title={filename}
    >
      <Paperclip size={11} className="shrink-0" />
      {href ? (
        <a
          href={href}
          download={filename}
          target="_blank"
          rel="noreferrer"
          aria-label={`Download ${filename}`}
          className="underline decoration-dotted hover:decoration-solid"
        >
          {display}
        </a>
      ) : (
        <span>{display}</span>
      )}
      {sz && <span className="text-[10px] text-[#7a1f2b]/70">· {sz}</span>}
    </span>
  );
}

/* --------- Admin status chip --------- */
const STATUS_COLORS = {
  "Approved":        "bg-[#e5f2eb] text-[#1e6b3c] border-[#1e6b3c]/40",
  "On Hold":         "bg-[#fdf0d8] text-[#8a5f12] border-[#8a5f12]/40",
  "Rejected":        "bg-[#f9e5e4] text-[#b3261e] border-[#b3261e]/40",
  "Quote Requested": "bg-[#e8f2f2] text-[#2d6b6b] border-[#2d6b6b]/40",
  "Processing":      "bg-[#efe5f8] text-[#6b3fa0] border-[#6b3fa0]/40",
  "Bill Paid":       "bg-[#e5f2eb] text-[#1e6b3c] border-[#1e6b3c]/40",
};
function _statusCls(status) {
  return STATUS_COLORS[status] || "bg-[#faf5eb] text-[#5a1520] border-[#5a1520]/40";
}
export function AdminStatusChip({ status, history }) {
  const cls = _statusCls(status);
  return (
    <span className="relative inline-block group" data-testid="admin-status-chip">
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-widest font-semibold ${cls}`}
      >
        <Clock size={10} /> {status || "—"}
      </span>
      {Array.isArray(history) && history.length > 0 && (
        <div
          className="hidden group-hover:block absolute z-20 left-0 top-full mt-1 w-64 bg-white border border-[#e2d7c3] rounded-md shadow-lg p-2 text-left"
          data-testid="admin-status-history"
        >
          <div className="text-[10px] uppercase tracking-widest text-[#2d6b6b] font-semibold mb-1">
            History
          </div>
          <ul className="space-y-1">
            {history.map((h, i) => (
              <li key={i} className="text-[11px] text-[#1f2320]">
                <div className="font-semibold">
                  {h.status || h.current || h.action || "—"}
                </div>
                <div className="text-[10px] text-[#6e7770]">
                  {h.actor || h.by || ""} · {h.timestamp || h.at || ""}
                </div>
                {h.remark && (
                  <div className="text-[10px] text-[#4a524c] italic">
                    “{_truncate(h.remark, 80)}”
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </span>
  );
}

/* --------- Attachment list --------- */
export function AttachmentList({ items }) {
  if (!Array.isArray(items) || items.length === 0)
    return <span className="text-gray-400">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((a, i) => (
        <AttachmentChip
          key={i}
          filename={a.filename}
          url={a.url || a.download_url}
          sizeBytes={a.size_bytes || a.size}
        />
      ))}
    </div>
  );
}

/* --------- Nested table (array of objects) --------- */
export function NestedObjectTable({ rows, depth = 0 }) {
  if (!rows || rows.length === 0) return <span className="text-gray-400">—</span>;
  if (depth >= MAX_DEPTH)
    return <span className="text-[#6e7770] italic">[Nested data]</span>;
  const cols = Array.from(
    rows.reduce((acc, r) => {
      Object.keys(r || {}).forEach((k) => acc.add(k));
      return acc;
    }, new Set()),
  );
  const scrollable = rows.length > 5;
  return (
    <div
      className={`overflow-x-auto rounded-md border border-[#e2d7c3] bg-white ${
        scrollable ? "max-h-72 overflow-y-auto" : ""
      }`}
    >
      <table className="w-full text-xs">
        <thead className="bg-[#faf5eb] text-[#5a1520] sticky top-0">
          <tr>
            {cols.map((c) => (
              <th key={c} className="text-left p-1.5 pl-2 font-semibold uppercase tracking-widest">
                {_humanLabel(c)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className={i % 2 ? "bg-[#faf5eb]/40" : ""}>
              {cols.map((c) => (
                <td key={c} className="p-1.5 pl-2 border-t border-[#e2d7c3] align-top">
                  {renderFieldValue(r?.[c], c, depth + 1)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* --------- Key-value block (object fallback) --------- */
export function KeyValueBlock({ obj, depth = 0 }) {
  if (!obj || typeof obj !== "object")
    return <span className="text-gray-400">—</span>;
  if (depth >= MAX_DEPTH)
    return <span className="text-[#6e7770] italic">[Nested data]</span>;
  const entries = Object.entries(obj);
  if (entries.length === 0) return <span className="text-gray-400">—</span>;
  return (
    <dl
      className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs"
      data-testid="key-value-block"
    >
      {entries.map(([k, v]) => (
        <>
          <dt key={`${k}-k`} className="text-[#2d6b6b] uppercase tracking-widest font-semibold">
            {_humanLabel(k)}
          </dt>
          <dd key={`${k}-v`} className="text-[#1f2320]">
            {renderFieldValue(v, k, depth + 1)}
          </dd>
        </>
      ))}
    </dl>
  );
}

/* --------- Primary dispatch --------- */
export function renderFieldValue(value, fieldKey, depth = 0) {
  if (value === null || value === undefined || value === "")
    return <span className="text-gray-400">—</span>;

  if (typeof value === "boolean") return <span>{value ? "Yes" : "No"}</span>;
  if (typeof value === "string" || typeof value === "number")
    return <span>{String(value)}</span>;

  if (depth >= MAX_DEPTH)
    return <span className="text-[#6e7770] italic">[Nested data]</span>;

  // Attachment-shaped object.
  if (_isLikelyAttachment(value)) {
    return (
      <AttachmentChip
        filename={value.filename}
        url={value.url || value.download_url}
        sizeBytes={value.size_bytes || value.size}
      />
    );
  }

  // admin_status-shaped object (both legacy `{current, history}` and new
  // `{status, history}`).
  if (_isAdminStatus(value)) {
    return (
      <AdminStatusChip
        status={value.status || value.current}
        history={value.history}
      />
    );
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-gray-400">—</span>;
    if (value.every((v) => _isLikelyAttachment(v))) {
      return <AttachmentList items={value} />;
    }
    if (value.every((v) => v !== null && typeof v !== "object")) {
      return <span>{value.join(", ")}</span>;
    }
    if (value.every((v) => v && typeof v === "object")) {
      return <NestedObjectTable rows={value} depth={depth} />;
    }
    // Mixed — render each item recursively.
    return (
      <ul className="list-disc ml-4 text-xs space-y-0.5">
        {value.map((v, i) => (
          <li key={i}>{renderFieldValue(v, `${fieldKey}[${i}]`, depth + 1)}</li>
        ))}
      </ul>
    );
  }

  // Unknown object — key/value fallback (never JSON.stringify).
  return <KeyValueBlock obj={value} depth={depth} />;
}
